import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  type Auth
} from 'firebase/auth'
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  CACHE_SIZE_UNLIMITED,
  type Firestore
} from 'firebase/firestore'
import { getStorage, type FirebaseStorage } from 'firebase/storage'
import { getFunctions, type Functions } from 'firebase/functions'
import { getMessaging, isSupported, type Messaging } from 'firebase/messaging'
import {
  getPerformance,
  trace as createTrace,
  type FirebasePerformance,
  type PerformanceTrace
} from 'firebase/performance'
import { logger } from './lib/logger'

// Firebase configuration interface
interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId?: string
  appId: string
  measurementId?: string
}

// Firebase configuration from environment variables
const firebaseConfig: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined
}

// Validate required Firebase configuration
const requiredKeys: (keyof FirebaseConfig)[] = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'appId']
const missingKeys = requiredKeys.filter(key => !firebaseConfig[key])
if (missingKeys.length > 0) {
  throw new Error(`Missing required Firebase configuration: ${missingKeys.join(', ')}. Please check your environment variables.`)
}

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig)

// Initialize services
export const auth: Auth = getAuth(app)

// Set auth persistence to IndexedDB for reliable persistence in PWAs
// This ensures users stay logged in across browser sessions and app restarts
setPersistence(auth, indexedDBLocalPersistence)
  .catch(() => {
    // Fallback to localStorage if IndexedDB is not available
    return setPersistence(auth, browserLocalPersistence)
  })
  .catch((error) => {
    logger.warn('Failed to set auth persistence', error)
  })

// Initialize Firestore with offline persistence using the modern API
// This enables IndexedDB persistence with multi-tab support
export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
    cacheSizeBytes: CACHE_SIZE_UNLIMITED
  })
})

logger.info('Firestore initialized with offline persistence')

export const storage: FirebaseStorage = getStorage(app)
export const functions: Functions = getFunctions(app, 'europe-west1') // Ireland region

// Initialize Firebase Messaging (conditionally, as it's not supported in all browsers)
let messaging: Messaging | null = null

// Async function to initialize messaging
async function initializeMessaging(): Promise<Messaging | null> {
  try {
    const supported = await isSupported()
    if (supported) {
      messaging = getMessaging(app)
      logger.info('Firebase Messaging initialized successfully')
      return messaging
    } else {
      logger.warn('Firebase Messaging is not supported in this browser')
      return null
    }
  } catch (error) {
    logger.error('Error initializing Firebase Messaging', error as Error)
    return null
  }
}

// Initialize messaging on module load
const messagingPromise = initializeMessaging()

// Export messaging instance (may be null if not supported)
export { messaging, messagingPromise }

// ============================================================================
// Firebase Performance Monitoring
// ============================================================================

/** Performance monitoring instance */
let performance: FirebasePerformance | null = null

/** Active traces map for managing custom performance traces */
const activeTraces = new Map<string, PerformanceTrace>()

/**
 * Initialize Firebase Performance Monitoring
 * Only enabled in production to avoid noise during development
 */
function initializePerformance(): FirebasePerformance | null {
  try {
    // Only enable performance monitoring in production
    if (!import.meta.env.PROD) {
      logger.info('Firebase Performance disabled in development mode')
      return null
    }

    performance = getPerformance(app)
    logger.info('Firebase Performance Monitoring initialized successfully')
    return performance
  } catch (error) {
    logger.error('Error initializing Firebase Performance Monitoring', error as Error)
    return null
  }
}

// Initialize performance on module load (async for SSR compatibility)
if (typeof window !== 'undefined') {
  initializePerformance()
}

/**
 * Start a custom performance trace
 * Use for measuring critical operations like clock-in, data loading, etc.
 *
 * @example
 * const trace = startTrace('clock_in_flow')
 * // ... perform operation ...
 * stopTrace('clock_in_flow')
 */
export function startTrace(traceName: string): PerformanceTrace | null {
  if (!performance) {
    logger.debug(`Performance trace '${traceName}' skipped (not initialized)`)
    return null
  }

  try {
    // Stop any existing trace with the same name
    if (activeTraces.has(traceName)) {
      stopTrace(traceName)
    }

    const trace = createTrace(performance, traceName)
    trace.start()
    activeTraces.set(traceName, trace)

    logger.debug(`Performance trace '${traceName}' started`)
    return trace
  } catch (error) {
    logger.error(`Failed to start trace '${traceName}'`, error as Error)
    return null
  }
}

/**
 * Stop a custom performance trace
 */
export function stopTrace(traceName: string): void {
  const trace = activeTraces.get(traceName)

  if (!trace) {
    logger.debug(`Performance trace '${traceName}' not found`)
    return
  }

  try {
    trace.stop()
    activeTraces.delete(traceName)
    logger.debug(`Performance trace '${traceName}' stopped`)
  } catch (error) {
    logger.error(`Failed to stop trace '${traceName}'`, error as Error)
    activeTraces.delete(traceName)
  }
}

/**
 * Add a custom attribute to an active trace
 */
export function setTraceAttribute(
  traceName: string,
  attributeName: string,
  attributeValue: string
): void {
  const trace = activeTraces.get(traceName)

  if (!trace) {
    logger.debug(`Cannot set attribute on trace '${traceName}' - not found`)
    return
  }

  try {
    trace.putAttribute(attributeName, attributeValue)
  } catch (error) {
    logger.error(`Failed to set attribute on trace '${traceName}'`, error as Error)
  }
}

/**
 * Increment a custom metric on an active trace
 */
export function incrementTraceMetric(
  traceName: string,
  metricName: string,
  incrementBy = 1
): void {
  const trace = activeTraces.get(traceName)

  if (!trace) {
    logger.debug(`Cannot increment metric on trace '${traceName}' - not found`)
    return
  }

  try {
    trace.incrementMetric(metricName, incrementBy)
  } catch (error) {
    logger.error(`Failed to increment metric on trace '${traceName}'`, error as Error)
  }
}

/**
 * Measure an async operation with Firebase Performance
 *
 * @example
 * const result = await measureOperation('fetch_sessions', async () => {
 *   return await sessionsService.getAll()
 * })
 */
export async function measureOperation<T>(
  operationName: string,
  operation: () => Promise<T>,
  attributes?: Record<string, string>
): Promise<T> {
  const trace = startTrace(operationName)

  // Set custom attributes if provided
  if (trace && attributes) {
    Object.entries(attributes).forEach(([key, value]) => {
      setTraceAttribute(operationName, key, value)
    })
  }

  try {
    const result = await operation()

    if (trace) {
      setTraceAttribute(operationName, 'status', 'success')
    }

    return result
  } catch (error) {
    if (trace) {
      setTraceAttribute(operationName, 'status', 'error')
      setTraceAttribute(operationName, 'error_type', (error as Error).name || 'unknown')
    }
    throw error
  } finally {
    stopTrace(operationName)
  }
}

/**
 * Pre-defined trace names for consistent naming across the app
 */
export const TraceNames = {
  // Clock operations
  CLOCK_IN: 'clock_in_operation',
  CLOCK_OUT: 'clock_out_operation',
  BREAK_START: 'break_start_operation',
  BREAK_END: 'break_end_operation',

  // Data loading
  LOAD_DASHBOARD: 'load_dashboard',
  LOAD_SESSIONS: 'load_sessions',
  LOAD_WORKERS: 'load_workers',
  LOAD_SITES: 'load_sites',
  LOAD_REPORTS: 'load_reports',

  // Auth operations
  LOGIN: 'login_operation',
  LOGOUT: 'logout_operation',
  REGISTER: 'register_operation',

  // Admin operations
  GENERATE_REPORT: 'generate_report',
  EXPORT_DATA: 'export_data',
  BULK_OPERATION: 'bulk_operation',

  // API calls
  API_CALL: 'api_call',
  FIRESTORE_QUERY: 'firestore_query',
} as const

export type TraceName = (typeof TraceNames)[keyof typeof TraceNames]

// Export performance instance for direct access if needed
export { performance }

export default app
